import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { observer } from 'mobx-react-lite';
import {
	Link,
	Switch,
	Redirect,
	Route,
	useRouteMatch,
	useLocation,
	useParams,
	useHistory,
} from 'react-router-dom';
import { Stepper, Step, StepLabel } from '@material-ui/core';
import {
	Container,
	Div,
	Spanner,
	Typography,
} from '@icecreamsocial/components';
import { CloseIcon, RouteChangeBlocker } from '../../../../presentational';
import { HeaderPortal } from '../../../Main';
import GeneralSettings from './sub-routes/GeneralSettings';
import OfferSettings from './sub-routes/OfferSettings';
import ShareSettings from './sub-routes/ShareSettings';
import PolicySettings from './sub-routes/PolicySettings';
import {
	useStore,
	useRouteConfig,
	useScrollTop,
} from '../../../../../utils/hooks';
import { useCampaign } from '../../../../../utils/hooks/useCampaign';
import { Campaign } from '../../../../../queries/campaigns';
import { useBusiness } from '../../../../providers/Business';

const CreateCampaign = () => {
	const store = useStore();
	const campaignFormRef = useRef<Partial<Campaign>>({});
	const { path, url } = useRouteMatch();
	const history = useHistory();
	const { pathname } = useLocation();
	const [navigateMessage, setNavigateMessage] = useState({
		title: '',
		message: '',
		confirmText: '',
		cancelText: '',
	});
	const [blockRoute, setBlockRoute] = useState(true);
	const { business } = useBusiness();
	const { data: userBusiness } = business;
	const { create } = useCampaign();

	const { mutateAsync: createCampaign, isLoading } = create;

	useScrollTop([pathname]);

	/**
	 * update the route message whenever the pathname changes
	 */
	useEffect(() => {
		setBlockRoute(true);
		resetNavigateMessage();
	}, [pathname]);

	const resetNavigateMessage = () => {
		const title = 'Exit Campaign Setup?';
		const message = "Your progress won't be saved";
		const confirmText = 'Discard Changes & Exit';
		const cancelText = 'Continue Setup';

		setNavigateMessage({ title, message, confirmText, cancelText });
	};

	const activeStep = determineStepFromPath(pathname);

	const handlePartialFormSubmit = (values, nextRoute) => {
		const name = values.campaignName || campaignFormRef.current.name;
		campaignFormRef.current = { ...campaignFormRef.current, name, ...values };
		setBlockRoute(false);
		setTimeout(() => history.push(nextRoute));
	};

	const handleFinalSubmit = async (values) => {
		campaignFormRef.current = { ...campaignFormRef.current, ...values };
		const campaign = await createCampaign(campaignFormRef.current);
		console.debug(campaign);
		setBlockRoute(false);
		setTimeout(() => history.push(`/campaigns/view/${campaign.id}/integrations`));
	};

	const createBackHandler = (returnTo) => () => {
		const title = 'Go to Previous Step?';
		const message = "Your progress on the current step won't be saved";
		const confirmText = 'Discard Changes & Go Back a Step';
		const cancelText = 'Continue Current Step';
		setNavigateMessage({ title, message, confirmText, cancelText });
		history.push(returnTo);
		setBlockRoute(true);
	};

	const { routes, links } = useRouteConfig([
		{
			component: () => <Redirect to={`${path}/general`} />,
		},
		{
			path: `general`,
			name: `General`,
			component: () => (
				<GeneralSettings
					handleSubmit={(values) =>
						handlePartialFormSubmit(values, `${path}/offer`)
					}
					returnTo={() => history.push('/campaigns')}
					savedValues={{
						locale: store.Orchestrators.Auth.locale,
						campaignName: campaignFormRef.current?.name,
						fromName: userBusiness?.name,
						...campaignFormRef.current,
					}}
				/>
			),
		},
		{
			path: `offer`,
			name: `Offer`,
			component: () => (
				<OfferSettings
					handleSubmit={(values) =>
						handlePartialFormSubmit(values, `${path}/share`)
					}
					returnTo={createBackHandler(`${path}/general`)}
					savedValues={{ ...campaignFormRef.current }}
				/>
			),
		},
		{
			path: `share`,
			name: `Share`,
			component: observer(() => (
				<ShareSettings
					handleSubmit={(values) =>
						handlePartialFormSubmit(values, `${path}/data-and-policy`)
					}
					returnTo={createBackHandler(`${path}/offer`)}
				/>
			)),
		},
		{
			path: `data-and-policy`,
			name: `Data & Policy`,
			component: observer(() => (
				<PolicySettings
					handleSubmit={(values) => handleFinalSubmit(values)}
					returnTo={createBackHandler(`${path}/share`)}
					isLoading={isLoading}
					disabled={isLoading}
					savedValues={{
						termsAndConditionsURL: userBusiness?.termsAndConditionsURL,
						privacyPolicyURL: userBusiness?.privacyPolicyURL,
					}}
				/>
			)),
		},
	]);

	useLayoutEffect(() => {
		store.Orchestrators.View.setSingleColumn();
		return () => {
			store.Orchestrators.View.setSingleColumn(false);
		};
	}, []);

	return (
		<>
			<HeaderPortal>
				<Spanner
					grid
					gridColumns="repeat(3, 1fr)"
					backgroundColor="brandDark"
					textColor="bodyInverse"
				>
					<Typography.Span
						as={Link}
						to="/campaigns"
						textColor="bodyInverse"
						display="flex"
						alignItems="center"
					>
						<CloseIcon marginRight="md" /> Cancel Setup
					</Typography.Span>
				</Spanner>
			</HeaderPortal>
			<Container>
				<Div
					as={Stepper}
					activeStep={activeStep}
					backgroundColor="transparent !important"
				>
					{links.slice(1).map(({ text }, i) => (
						<Step key={i}>
							<StepLabel>{text}</StepLabel>
						</Step>
					))}
				</Div>
				<Switch>
					{routes.map(({ link, ...route }, i) => (
						<Route key={i} {...route} />
					))}
				</Switch>
			</Container>
			<RouteChangeBlocker
				when={blockRoute}
				title={navigateMessage.title}
				message={navigateMessage.message}
				confirmText={navigateMessage.confirmText}
				cancelText={navigateMessage.cancelText}
				beforeCancelNavigation={resetNavigateMessage}
			/>
		</>
	);
};
CreateCampaign.displayName = 'CreateCampaign';

function determineStepFromPath(path) {
	const steps = {
		general: 0,
		offer: 1,
		share: 2,
		'data-and-policy': 3,
	};
	const key = path.split('/create/')[1];

	return steps[key];
}

export default observer(CreateCampaign);
